import React, { useRef, useState } from 'react';
import { RoomInput, SpacingResult, luminaireGridPositions, wallOffsets } from '../lib/iluminacion';

// ============================================================
// Vista de planta (SVG) de un local con sus luminarias posicionadas.
// Soporta arrastre con eventos de mouse nativos (sin librerías de DnD —
// no existe ningún patrón de arrastre previo en el repo, confirmado con
// graphify antes de escribir este componente).
//
// CONVENCIÓN DE EJES (la geometría sale de luminaireGridPositions() en
// src/lib/iluminacion.ts, compartida con la vista 3D, el PDF y el cálculo
// de uniformidad de src/lib/ies-photometric.ts):
//   - Eje horizontal del SVG (izquierda→derecha) = room.ancho
//   - Eje vertical del SVG (arriba→abajo)        = room.largo
// Es decir: x en metros = desplazamientoParedX + columna · espaciadoX
//           y en metros = desplazamientoParedY + fila    · espaciadoY
// ============================================================

const VIEWBOX_WIDTH = 600;
const VIEWBOX_HEIGHT = 400;
// Margen aumentado (antes 40) para dejar espacio a las dos filas/columnas de
// cotas de espaciado/pared que se dibujan fuera del rectángulo del local.
const MARGIN = 60;
const CLAMP_MARGIN_METERS = 0.1;

// Estilo de las líneas de cota (espaciado entre luminarias y distancia a
// pared) — color sutil (mismo tono que el borde del local) para la línea,
// y un tono más claro/contrastante para la etiqueta numérica.
const DIM_LINE_COLOR = '#475569';
const DIM_LABEL_COLOR = '#cbd5e1';
const DIM_TICK = 5; // px — largo de las marcas de extensión en los extremos

export interface PlantScale {
  scale: number;       // px por metro
  offsetX: number;     // px — esquina izquierda del rectángulo del local dentro del viewBox
  offsetY: number;     // px — esquina superior del rectángulo del local dentro del viewBox
  roomWidthPx: number;  // ancho del local dibujado, en px
  roomHeightPx: number; // largo del local dibujado, en px
}

export interface LuminairePositionPx {
  index: number;
  x: number; // px, dentro del viewBox
  y: number; // px, dentro del viewBox
}

export interface LuminairePositionMeters {
  index: number;
  xMeters: number;
  yMeters: number;
}

// Calcula el factor de escala que ajusta el local (room.ancho x room.largo)
// dentro de un viewBox fijo con margen, preservando la proporción real —
// nunca estira ni comprime el local de forma desigual en los dos ejes.
export function calculatePlantScale(
  room: RoomInput,
  viewBoxWidth: number = VIEWBOX_WIDTH,
  viewBoxHeight: number = VIEWBOX_HEIGHT,
  margin: number = MARGIN
): PlantScale {
  const usableWidth = viewBoxWidth - margin * 2;
  const usableHeight = viewBoxHeight - margin * 2;

  if (room.ancho <= 0 || room.largo <= 0 || usableWidth <= 0 || usableHeight <= 0) {
    return { scale: 0, offsetX: margin, offsetY: margin, roomWidthPx: 0, roomHeightPx: 0 };
  }

  const scale = Math.min(usableWidth / room.ancho, usableHeight / room.largo);
  const roomWidthPx = room.ancho * scale;
  const roomHeightPx = room.largo * scale;

  // Centra el rectángulo del local dentro del área útil (el eje que no
  // limita la escala queda con espacio sobrante repartido simétricamente).
  const offsetX = margin + (usableWidth - roomWidthPx) / 2;
  const offsetY = margin + (usableHeight - roomHeightPx) / 2;

  return { scale, offsetX, offsetY, roomWidthPx, roomHeightPx };
}

// Calcula las posiciones en píxeles (dentro del viewBox) de cada luminaria
// de la grilla, a partir del resultado de calculateSpacing(). Dibuja la
// grilla filas×columnas completa — esa cantidad (numeroLuminariasInstaladas)
// es la que realmente se instala para mantener una grilla uniforme, incluso
// cuando supera el mínimo teórico normativo (ver calculateFullReport en
// iluminacion.ts).
export function calculateLuminairePositionsPx(
  espaciado: SpacingResult,
  plantScale: PlantScale
): LuminairePositionPx[] {
  if (plantScale.scale <= 0) {
    return [];
  }

  return luminaireGridPositions(espaciado).map((m, index) => ({
    index,
    x: plantScale.offsetX + m.x * plantScale.scale,
    y: plantScale.offsetY + m.y * plantScale.scale,
  }));
}

// Inversa de la conversión metros→px usada en calculateLuminairePositionsPx.
export function pxToMeters(
  pxX: number,
  pxY: number,
  plantScale: PlantScale
): { xMeters: number; yMeters: number } {
  if (plantScale.scale <= 0) return { xMeters: 0, yMeters: 0 };
  return {
    xMeters: (pxX - plantScale.offsetX) / plantScale.scale,
    yMeters: (pxY - plantScale.offsetY) / plantScale.scale,
  };
}

// Restringe una posición (en metros) para que la luminaria nunca quede fuera
// del rectángulo del local, dejando un pequeño margen respecto al borde.
export function clampToRoom(
  xMeters: number,
  yMeters: number,
  room: RoomInput
): { xMeters: number; yMeters: number } {
  const clampAxis = (value: number, max: number): number => {
    const min = CLAMP_MARGIN_METERS;
    const upper = Math.max(min, max - CLAMP_MARGIN_METERS);
    return Math.min(Math.max(value, min), upper);
  };

  return {
    xMeters: clampAxis(xMeters, room.ancho),
    yMeters: clampAxis(yMeters, room.largo),
  };
}

export interface LuminairePlantViewProps {
  room: RoomInput;
  espaciado: SpacingResult;
  // numeroLuminariasInstaladas = filas × columnas (grilla completa uniforme,
  // ver calculateFullReport en iluminacion.ts). No se usa para recortar la
  // grilla — se dibujan siempre todas las posiciones filas×columnas.
  numeroLuminariasInstaladas: number;
  // Posiciones ajustadas manualmente, indexadas por el mismo index que
  // genera calculateLuminairePositionsPx.
  customPositions?: Record<number, { xMeters: number; yMeters: number }>;
  // Se dispara cuando el usuario suelta una luminaria en una nueva posición.
  onPositionChange?: (index: number, xMeters: number, yMeters: number) => void;
}

export default function LuminairePlantView({
  room,
  espaciado,
  customPositions,
  onPositionChange,
}: LuminairePlantViewProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [dragState, setDragState] = useState<LuminairePositionMeters | null>(null);

  const plantScale = calculatePlantScale(room);
  const luminairePositions = calculateLuminairePositionsPx(espaciado, plantScale);
  const sinDatos = espaciado.filas === 0 || espaciado.columnas === 0;

  const { offsetX, offsetY, roomWidthPx, roomHeightPx } = plantScale;

  // Convierte coordenadas de mouse (client) a coordenadas del viewBox del SVG,
  // compensando la diferencia entre el tamaño renderizado (CSS) y el viewBox.
  const clientToViewBox = (clientX: number, clientY: number): { pxX: number; pxY: number } | null => {
    const svgEl = svgRef.current;
    if (!svgEl) return null;
    const rect = svgEl.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    const scaleX = VIEWBOX_WIDTH / rect.width;
    const scaleY = VIEWBOX_HEIGHT / rect.height;
    return {
      pxX: (clientX - rect.left) * scaleX,
      pxY: (clientY - rect.top) * scaleY,
    };
  };

  const handleLuminaireMouseDown = (index: number) => {
    const pos = luminairePositions.find(p => p.index === index);
    if (!pos) return;
    const custom = customPositions?.[index];
    const startMeters = custom ?? pxToMeters(pos.x, pos.y, plantScale);
    setDragState({ index, xMeters: startMeters.xMeters, yMeters: startMeters.yMeters });
  };

  const handleSvgMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!dragState) return;
    const viewBoxPoint = clientToViewBox(e.clientX, e.clientY);
    if (!viewBoxPoint) return;
    const meters = pxToMeters(viewBoxPoint.pxX, viewBoxPoint.pxY, plantScale);
    const clamped = clampToRoom(meters.xMeters, meters.yMeters, room);
    setDragState({ index: dragState.index, xMeters: clamped.xMeters, yMeters: clamped.yMeters });
  };

  const endDrag = () => {
    if (!dragState) return;
    onPositionChange?.(dragState.index, dragState.xMeters, dragState.yMeters);
    setDragState(null);
  };

  // Cota horizontal (eje X, arriba del local): línea fina con flechas en
  // ambos extremos + marcas de extensión + etiqueta con el valor en metros.
  const renderHorizontalDimension = (key: string, xStart: number, xEnd: number, y: number, label: string) => {
    const xa = Math.min(xStart, xEnd);
    const xb = Math.max(xStart, xEnd);
    return (
      <g key={key} pointerEvents="none">
        <line x1={xa} y1={y - DIM_TICK} x2={xa} y2={y + DIM_TICK} stroke={DIM_LINE_COLOR} strokeWidth={1} />
        <line x1={xb} y1={y - DIM_TICK} x2={xb} y2={y + DIM_TICK} stroke={DIM_LINE_COLOR} strokeWidth={1} />
        <line
          x1={xa} y1={y} x2={xb} y2={y}
          stroke={DIM_LINE_COLOR} strokeWidth={1}
          markerStart="url(#dimArrowStart)" markerEnd="url(#dimArrowEnd)"
        />
        <text x={(xa + xb) / 2} y={y - 4} textAnchor="middle" fontSize={9} fill={DIM_LABEL_COLOR}>
          {label}
        </text>
      </g>
    );
  };

  // Cota vertical (eje Y, a un costado del local) — misma lógica rotada.
  const renderVerticalDimension = (key: string, yStart: number, yEnd: number, x: number, label: string) => {
    const ya = Math.min(yStart, yEnd);
    const yb = Math.max(yStart, yEnd);
    const midY = (ya + yb) / 2;
    return (
      <g key={key} pointerEvents="none">
        <line x1={x - DIM_TICK} y1={ya} x2={x + DIM_TICK} y2={ya} stroke={DIM_LINE_COLOR} strokeWidth={1} />
        <line x1={x - DIM_TICK} y1={yb} x2={x + DIM_TICK} y2={yb} stroke={DIM_LINE_COLOR} strokeWidth={1} />
        <line
          x1={x} y1={ya} x2={x} y2={yb}
          stroke={DIM_LINE_COLOR} strokeWidth={1}
          markerStart="url(#dimArrowStart)" markerEnd="url(#dimArrowEnd)"
        />
        <text
          x={x - 6} y={midY}
          textAnchor="middle" fontSize={9} fill={DIM_LABEL_COLOR}
          transform={`rotate(-90, ${x - 6}, ${midY})`}
        >
          {label}
        </text>
      </g>
    );
  };

  // Posiciones de grilla (sin dragging) usadas como referencia para las cotas
  // — reutilizan directamente espaciado.espaciadoX/Y y la distancia a pared
  // de CADA eje (wallOffsets: X en la cota horizontal, Y en la vertical) ya
  // calculados en calculateSpacing() (lib/iluminacion.ts), sin recalcular nada.
  const pared = wallOffsets(espaciado);
  const pos00 = luminairePositions[0];
  const pos01 = espaciado.columnas >= 2 ? luminairePositions[1] : undefined;
  const pos10 = espaciado.filas >= 2 ? luminairePositions[espaciado.columnas] : undefined;

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
      width="100%"
      // user-select: none — al arrastrar una luminaria el navegador no debe
      // seleccionar/resaltar el texto de las cotas.
      style={{ background: '#0f172a', borderRadius: 8, border: '1px solid #334155', userSelect: 'none', WebkitUserSelect: 'none' }}
      onMouseMove={handleSvgMouseMove}
      onMouseUp={endDrag}
      onMouseLeave={endDrag}
    >
      <defs>
        <marker id="dimArrowEnd" viewBox="0 0 10 10" refX={8} refY={5} markerWidth={6} markerHeight={6} orient="auto">
          <path d="M0,0 L10,5 L0,10 z" fill={DIM_LINE_COLOR} />
        </marker>
        <marker id="dimArrowStart" viewBox="0 0 10 10" refX={8} refY={5} markerWidth={6} markerHeight={6} orient="auto-start-reverse">
          <path d="M0,0 L10,5 L0,10 z" fill={DIM_LINE_COLOR} />
        </marker>
      </defs>

      {/* Contorno del local */}
      <rect
        x={offsetX}
        y={offsetY}
        width={roomWidthPx}
        height={roomHeightPx}
        fill="#1e293b"
        stroke="#475569"
        strokeWidth={1.5}
      />

      {/* Cota de ancho total (eje horizontal) — empujada más afuera para dejar
          espacio a las cotas de espaciado/pared entre luminarias, más cercanas
          al local */}
      <text
        x={offsetX + roomWidthPx / 2}
        y={offsetY - 46}
        textAnchor="middle"
        fontSize={11}
        fill="#94a3b8"
      >
        {room.ancho.toFixed(2)} m
      </text>

      {/* Cota de largo total (eje vertical) — misma lógica, empujada afuera */}
      <text
        x={offsetX - 46}
        y={offsetY + roomHeightPx / 2}
        textAnchor="middle"
        fontSize={11}
        fill="#94a3b8"
        transform={`rotate(-90, ${offsetX - 46}, ${offsetY + roomHeightPx / 2})`}
      >
        {room.largo.toFixed(2)} m
      </text>

      {!sinDatos && pos00 && (
        <>
          {/* Distancia horizontal de la pared a la luminaria más cercana */}
          {renderHorizontalDimension(
            'dim-wall-x', offsetX, pos00.x, offsetY - 14,
            `${pared.x.toFixed(2)} m`
          )}
          {/* Espaciado horizontal entre luminarias adyacentes de la misma fila */}
          {pos01 && renderHorizontalDimension(
            'dim-spacing-x', pos00.x, pos01.x, offsetY - 30,
            `${espaciado.espaciadoX.toFixed(2)} m`
          )}
          {/* Distancia vertical de la pared a la luminaria más cercana */}
          {renderVerticalDimension(
            'dim-wall-y', offsetY, pos00.y, offsetX - 14,
            `${pared.y.toFixed(2)} m`
          )}
          {/* Espaciado vertical entre luminarias adyacentes de la misma columna */}
          {pos10 && renderVerticalDimension(
            'dim-spacing-y', pos00.y, pos10.y, offsetX - 30,
            `${espaciado.espaciadoY.toFixed(2)} m`
          )}
        </>
      )}

      {/* Cota de altura del plano de trabajo — esquina inferior derecha */}
      <text
        x={offsetX + roomWidthPx}
        y={offsetY + roomHeightPx + 20}
        textAnchor="end"
        fontSize={10}
        fill="#64748b"
      >
        {`Plano de trabajo: ${room.alturaPlanoTrabajo.toFixed(2)} m`}
      </text>

      {sinDatos ? (
        <text
          x={VIEWBOX_WIDTH / 2}
          y={VIEWBOX_HEIGHT / 2}
          textAnchor="middle"
          fontSize={13}
          fill="#94a3b8"
        >
          Sin datos suficientes para posicionar luminarias
        </text>
      ) : (
        luminairePositions.map(pos => {
          const isDragging = dragState?.index === pos.index;
          const custom = customPositions?.[pos.index];

          let displayX = pos.x;
          let displayY = pos.y;
          let isReposicionada = false;

          if (isDragging && dragState) {
            displayX = plantScale.offsetX + dragState.xMeters * plantScale.scale;
            displayY = plantScale.offsetY + dragState.yMeters * plantScale.scale;
            isReposicionada = true;
          } else if (custom) {
            displayX = plantScale.offsetX + custom.xMeters * plantScale.scale;
            displayY = plantScale.offsetY + custom.yMeters * plantScale.scale;
            isReposicionada = true;
          }

          return (
            <React.Fragment key={pos.index}>
              <circle
                cx={displayX}
                cy={displayY}
                r={8}
                fill={isReposicionada ? '#3b82f6' : '#fbbf24'}
                style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                onMouseDown={() => handleLuminaireMouseDown(pos.index)}
              >
                <title>{`Luminaria ${pos.index + 1}`}</title>
              </circle>

              {isDragging && dragState && (
                <g pointerEvents="none">
                  <rect
                    x={displayX + 12}
                    y={displayY - 26}
                    width={90}
                    height={20}
                    rx={4}
                    fill="#0f172a"
                    stroke="#334155"
                  />
                  <text
                    x={displayX + 18}
                    y={displayY - 12}
                    fontSize={10}
                    fill="#e2e8f0"
                  >
                    {`x: ${dragState.xMeters.toFixed(1)}m y: ${dragState.yMeters.toFixed(1)}m`}
                  </text>
                </g>
              )}
            </React.Fragment>
          );
        })
      )}
    </svg>
  );
}
